import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, Send, Instagram, Facebook, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { contactAPI } from '../lib/api';
import { toast } from 'sonner';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const myPhoneNumber = '556999914037';
  const myEmail = 'empregamaisrondonia@gmail.com';

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação robusta
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setLoading(true);

    try {
      // 1. Tenta salvar no banco de dados via API
      await contactAPI.submit(formData);
      toast.success('Sua mensagem foi registrada em nosso sistema!');

      // 2. Prepara o link do WhatsApp como segunda via de contato
      const textForWhatsapp = `*Nova Mensagem do Site Emprega Mais RO*%0A%0A` +
        `*Nome:* ${formData.name}%0A` +
        `*Email:* ${formData.email}%0A` +
        `*Assunto:* ${formData.subject}%0A` +
        `*Mensagem:* ${formData.message}`;

      const whatsappUrl = `https://wa.me/${myPhoneNumber}?text=${textForWhatsapp}`;
      
      // Delay suave para o usuário ler o toast antes de abrir o WhatsApp
      setTimeout(() => {
        window.open(whatsappUrl, '_blank');
      }, 1200);

      // Limpa o formulário
      setFormData({ name: '', email: '', subject: '', message: '' });

    } catch (err) {
      console.error("Erro no envio:", err);
      toast.error('Ocorreu um erro ao salvar, mas você pode nos chamar no WhatsApp!');
      
      // Fallback: Abre o WhatsApp mesmo se a API falhar
      const fallbackText = `*Nova Mensagem (via Site)*%0A%0ANome: ${formData.name}%0AMensagem: ${formData.message}`;
      window.open(`https://wa.me/${myPhoneNumber}?text=${encodeURIComponent(fallbackText)}`, '_blank');
    } finally {
      setLoading(false);
    }
  };

  const whatsappSimpleLink = `https://wa.me/${myPhoneNumber}?text=${encodeURIComponent('Olá! Vim pelo site Emprega Mais RO e gostaria de tirar uma dúvida.')}`;
  const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${myEmail}`;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 md:px-8 transition-colors duration-300">
      <div className="container mx-auto max-w-6xl">
        
        {/* Header Institucional - Importante para o Google Adsense */}
        <div className="text-center mb-12 md:mb-16 animate-in fade-in slide-in-from-bottom-4">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-slate-800 dark:text-slate-100 mb-4">
            Sobre o Emprega Mais RO & Contato
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            O <strong>Emprega Mais Rondônia</strong> é o principal portal de conexão entre talentos e oportunidades no estado. 
            Nossa missão é facilitar o acesso ao mercado de trabalho de forma gratuita e transparente. 
            Tem alguma dúvida ou sugestão? Estamos prontos para te atender.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          
          {/* LADO ESQUERDO: INFOS */}
          <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-500 delay-100">
            
            <a 
              href={whatsappSimpleLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block group relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 md:p-8 text-white shadow-lg shadow-green-500/20 hover:shadow-green-500/30 transition-all hover:-translate-y-1"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <MessageCircle size={120} />
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">WhatsApp Comercial</h3>
                  <p className="text-green-100 text-sm md:text-base mb-1">Resposta rápida para dúvidas urgentes.</p>
                  <p className="text-2xl font-bold text-white mt-2 group-hover:underline decoration-2 underline-offset-4">
                    (69) 99991-4037
                  </p>
                </div>
              </div>
            </a>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a 
                href={gmailLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800 hover:border-teal-500/30 transition-all group block hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center mb-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-1 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">Email</h3>
                <span className="text-sm text-slate-500 dark:text-slate-400 break-all block">
                  {myEmail}
                </span>
              </a>

              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800 hover:border-teal-500/30 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-1">Localização</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Rondônia, Brasil<br/>Atendimento Online
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
              <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-4">Siga-nos</h3>
              <div className="flex gap-4">
                <a 
                  href="https://instagram.com/empregamaisro"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white transition-all duration-300 w-full justify-center sm:justify-start group shadow-sm hover:shadow-purple-500/20"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="font-medium">Instagram</span>
                </a>

                <a 
                  href="https://facebook.com/empregamaisro"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-700 hover:text-white transition-all duration-300 w-full justify-center sm:justify-start group shadow-sm hover:shadow-blue-600/20"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="font-medium">Facebook</span>
                </a>
              </div>
            </div>
          </div>

          {/* FORMULÁRIO */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-10 shadow-lg shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-800 animate-in fade-in slide-in-from-right-4 duration-500 delay-200 h-fit">
            <h2 className="text-2xl font-bold font-heading text-slate-800 dark:text-slate-100 mb-2">
              Envie uma mensagem
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm">
              Preencha o formulário abaixo e entraremos em contato o mais breve possível.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 font-medium">Nome Completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: João da Silva"
                  className="rounded-xl h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-medium">Email para contato</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Ex: joao@email.com"
                  className="rounded-xl h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-slate-700 dark:text-slate-300 font-medium">Assunto</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ex: Dúvida sobre vaga"
                  className="rounded-xl h-12 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-700 dark:text-slate-300 font-medium">Mensagem</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Escreva sua mensagem aqui..."
                  rows={5}
                  className="rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500 resize-none p-4"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-6 font-bold text-lg shadow-lg shadow-teal-600/20 hover:scale-[1.01] transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2"
              >
                {loading ? 'Enviando...' : 'Enviar Mensagem'}
                {!loading && <Send className="w-5 h-5 ml-2" />}
              </Button>
            </form>
            
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-6 text-center leading-relaxed">
              Ao enviar, você concorda em ser contatado pela nossa equipe. Seus dados estão seguros conosco.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;